import { Injectable } from '@angular/core';
import { Resolve, Router } from '@angular/router';

import { Session } from '../../model/session.class';
import { AccountService } from '../shell/account.service';
import { AuthService } from '../shell/auth.service';
import { SessionService } from '../shell/session.service';

interface CspQueryParams {
  cloud_username: string;
  cloud_password: string;
  installid: string;
}

@Injectable()
export class SessionResolver implements Resolve<Session> {
  private readonly STORAGE_KEY = 'csp_query_parameters';

  constructor(
    private auth: AuthService,
    private currentAccount: AccountService,
    private currentSession: SessionService,
    private router: Router
  ) {}

  public resolve(): Promise<any> {
    let params: Partial<CspQueryParams> = this.parseQueryString(location.search);
    if (this.hasRequiredParams(params)) {
      this.storeParams(params);
    } else {
      params = this.loadStoredParams() || params;
    }
    if (!this.hasRequiredParams(params)) {
      this.router.navigate(['error'], { state: { codeStatus: 400 } });

      return Promise.reject(new Error('Missing required session parameters'));
    }

    return this.auth.getAuthToken(params.cloud_username, params.cloud_password).then(() => {
      this.currentAccount.current.setAccount(params.cloud_username, params.cloud_password, params.installid);

      return this.currentSession.getSession(params.installid);
    });
  }

  private parseQueryString(qs: string): Partial<CspQueryParams> {
    const query = new URLSearchParams(qs);

    return {
      cloud_username: query.get('cloud_username') ?? undefined,
      cloud_password: query.get('cloud_password') ?? undefined,
      installid: query.get('installid') ?? undefined,
    };
  }

  private hasRequiredParams(params: Partial<CspQueryParams>): params is CspQueryParams {
    return !!(params.cloud_username && params.cloud_password && params.installid);
  }

  private storeParams(params: object): void {
    try {
      sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(params));
    } catch (e) {
      console.warn('Session Storage not available. setItem() failed.');
      console.error(e);
    }
  }

  private loadStoredParams(): Partial<CspQueryParams> | null {
    try {
      const stored = sessionStorage.getItem(this.STORAGE_KEY);

      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  }
}
